import { useEffect, useState } from 'react';

// The same edges Tailwind uses (md = 768, lg = 1024), so a component that asks
// "am I on a phone" in JS and a class that says `md:` in CSS can never disagree
// about which side of the line a screen is on.
const TABLET_MIN = 768;
const DESKTOP_MIN = 1024;

// `screen` on purpose: a printed A4 page is ~718px wide, and without it a
// desktop print would re-render every report as phone cards.
const QUERIES = {
  phone: `screen and (max-width: ${TABLET_MIN - 0.02}px)`,
  tablet: `screen and (min-width: ${TABLET_MIN}px) and (max-width: ${DESKTOP_MIN - 0.02}px)`,
};

function read() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'desktop';
  if (window.matchMedia(QUERIES.phone).matches) return 'phone';
  if (window.matchMedia(QUERIES.tablet).matches) return 'tablet';
  return 'desktop';
}

/**
 * `phone` (< 768), `tablet` (768–1023) or `desktop` (≥ 1024).
 *
 * Layout should stay in CSS wherever it can — a class costs no render. This is
 * for the cases CSS cannot express: a table that becomes a different element
 * tree on a phone, or a default that depends on the device.
 */
export default function useBreakpoint() {
  const [bp, setBp] = useState(read);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const lists = Object.values(QUERIES).map((q) => window.matchMedia(q));
    const update = () => setBp(read());
    lists.forEach((l) => l.addEventListener('change', update));
    return () => lists.forEach((l) => l.removeEventListener('change', update));
  }, []);

  return {
    bp,
    isPhone: bp === 'phone',
    isTablet: bp === 'tablet',
    isDesktop: bp === 'desktop',
    isTouchSized: bp !== 'desktop',
  };
}
